import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    Box,
    Typography,
    Button,
    Tooltip,
    AccordionDetails,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import CostIcon from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/CostIcon';
import EchelonsHypothesesModal from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/components/EchelonsHypotheses/EchelonsHypothesesModal';
import { CostPerYearT } from '../../../GVTData/helpers/hooks/gvtData';
import {
    StyAccordionCost,
    StyAccordionSummaryCost,
} from '../PayConstantsEntryForm/TotalCost';

const StyBlock = styled(Box)(() => ({
    backgroundColor: 'transparent',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
}));

type Props = {
    hrSimulation: HrSimulation;
    isHrSimulationResponseFetching: boolean;
    echelonTotalCost: number;
    echelonPerYear: CostPerYearT;
    isProjection: boolean;
};

const EchelonDetail = ({
    isHrSimulationResponseFetching,
    title,
    tooltip,
    cost,
    costPerYear,
}: {
    isHrSimulationResponseFetching: boolean;
    title: string;
    tooltip?: string;
    cost: number;
    costPerYear: CostPerYearT;
}) => (
    <StyBlock>
        <Typography
            variant="body1"
            sx={{ fontSize: '11px', fontWeight: 'bold' }}
        >
            {title}
            {tooltip && (
                <Tooltip title={tooltip} key="button-element">
                    <InfoIcon color="primary" sx={{ fontSize: 16 }} />
                </Tooltip>
            )}
        </Typography>
        <StyAccordionCost>
            <StyAccordionSummaryCost>
                <CostIcon
                    value={cost}
                    isLoading={isHrSimulationResponseFetching}
                    isInAccordion={true}
                />
            </StyAccordionSummaryCost>
            <AccordionDetails
                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                {costPerYear &&
                    Object.entries(costPerYear).map(([year, costPY]) => (
                        <CostIcon
                            key={year}
                            value={costPY}
                            isLoading={isHrSimulationResponseFetching}
                            year={Number(year)}
                        />
                    ))}
            </AccordionDetails>
        </StyAccordionCost>
    </StyBlock>
);

const EchelonsOverview = ({
    hrSimulation,
    isHrSimulationResponseFetching,
    echelonTotalCost,
    echelonPerYear,
    isProjection,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    const [showModal, setShowModal] = useState(false);

    return (
        <Grid container sx={{ height: '100%', padding: '12px' }}>
            <Grid item xs={12} sx={{ height: '7%', minHeight: '0' }}>
                <Typography variant="h4" sx={{ fontSize: '16px' }}>
                    {t('humanResources.simulation.echelonRaise')}
                </Typography>
            </Grid>
            <Grid
                item
                xs={12}
                sx={{
                    height: isProjection ? '40%' : '82%',
                    minHeight: '0',
                    marginTop: '15px',
                }}
            >
                <EchelonDetail
                    title={t('humanResources.simulation.raiseCost')}
                    isHrSimulationResponseFetching={
                        isHrSimulationResponseFetching
                    }
                    cost={echelonTotalCost}
                    costPerYear={echelonPerYear}
                />
            </Grid>
            <Grid item xs={12} sx={{ height: '10%', minHeight: '0' }}>
                <Button
                    size="small"
                    onClick={() => setShowModal(true)}
                    variant="text"
                    sx={{ fontSize: '12px' }}
                >
                    {t('humanResources.simulation.agentsDetail')}
                </Button>
            </Grid>
            {showModal && (
                <EchelonsHypothesesModal
                    hrSimulation={hrSimulation}
                    showModal={showModal}
                    setShowModal={setShowModal}
                />
            )}
        </Grid>
    );
};

export default memo(EchelonsOverview);
